import React, { useState, useContext } from "react";
import { Button } from "antd";
import { ModalContext } from "context/ModalContext";
import styled from "styled-components";
import { useHistory, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { fetch_product_details } from "services/apiSrv";
import PolicyConfigForm from "components/widgets/forms/setup/PolicyConfigForm";
import AddProductBenefit from "components/widgets/modals/AddProductBenefit";
import BasicDetails from "components/setup/BasicDetails";

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 20px;
  margin-bottom: 16px;
  color: #333;
`;

const Subtitle = styled.h2`
  font-size: 18px;
  margin-bottom: 12px;
  color: #555;
`;

const Section = styled.div`
  margin-bottom: 48px;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  max-height: 60vh;
  overflow-y: auto;
  margin-bottom: 32px;
  background: #fff;
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
    font-size: 10px;
  }

  th {
    background: #f4f4f4;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  tr:hover {
    background-color: #f9f9f9;
  }
`;

const DetailsSection = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 32px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const DetailsCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  flex: 1;
  min-width: 300px;
`;

const InfoText = styled.p`
  margin: 8px 0;
  font-size: 10px;
  color: #666;
`;

const ActionButton = styled(Button)`
  background-color: #1890ff;
  border-color: #1890ff;
  color: #fff;
  border-radius: 4px;
  margin-top: 8px;
  &:hover {
    background-color: #40a9ff;
    border-color: #40a9ff;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 16px;
  margin: 16px 0;
`;

const LoadingMessage = styled.div`
  font-size: 16px;
  color: #666;
  margin: 16px 0;
`;

const SaveButton = styled(Button)`
  background-color: #52c41a;
  border-color: #52c41a;
  color: #fff;
  border-radius: 4px;
  padding: 8px 24px;
  &:hover {
    background-color: #73d13d;
    border-color: #73d13d;
  }
`;

export default function PolicyConfigPage() {
  const { setModalShow, modalShow, modalClose } = useContext(ModalContext);
  const history = useHistory();
  const { id: productSystemRef } = useParams();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['productDetails', productSystemRef],
    queryFn: () => fetch_product_details(productSystemRef),
    enabled: !!productSystemRef,
    retry: 2,
    staleTime: 5 * 60 * 1000,
  });

  const handleOverallSave = () => {
    alert("Overall Save triggered! (UI only)");
  };

  const addBenefit = () => {
    setModalShow(true);
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingMessage>Loading product details...</LoadingMessage>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <ErrorMessage>Error fetching product details: {error?.message || 'Something went wrong'}</ErrorMessage>
      </Container>
    );
  }

  if (!data?.data) {
    return (
      <Container>
        <ErrorMessage>No product data available.</ErrorMessage>
      </Container>
    );
  }

  const { productName, underwriter, productBenefits, productDescription, insuranceClass, basePremiumRate, productPremium, productCover } = data.data[0];

  return (
    <Container>
      {/* Product Details */}
      {/* <Section>
        <DetailsSection>
          <DetailsCard>
            <Subtitle>Basic Details</Subtitle>
            <InfoText>Name: {productName || 'N/A'}</InfoText>
            <InfoText>Description: {productDescription || 'No description available'}</InfoText>
            <InfoText>Premium Maximum: {productPremium?.maximum || 'N/A'}</InfoText>
            <InfoText>Premium Minimum: {productPremium?.minimum || 'N/A'}</InfoText>
            <InfoText>Rate Type: {basePremiumRate?.rateType || 'N/A'}</InfoText>
            <InfoText>Rate Value: {basePremiumRate?.value || 'N/A'}</InfoText>
          </DetailsCard>
        </DetailsSection>
      </Section> */}

      <BasicDetails />

      {/* Product Benefits */}
      <Section>
        <Title>Product Benefits</Title>
        <ActionButton onClick={addBenefit}>Add Benefit</ActionButton>
        <TableWrapper>
          {productBenefits && productBenefits.length > 0 ? (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Calculation Type</th>
                  <th>Type</th>
                  <th>Reference</th>
                  <th>Benefit Days Max</th>
                  <th>Benefit Days Min</th>
                  <th>Default Days</th>
                  <th>Adjustable (Daily)</th>
                  <th>Adjustable (Limit)</th>
                </tr>
              </thead>
              <tbody>
                {productBenefits.map((benefit, index) => (
                  <tr key={index}>
                    {/* <td>{benefit.benefitInternaleRef}</td>   */}
                    <td>{benefit.benefit_category_name || 'N/A'}</td>
                    <td>{benefit.benefitCalculationtype || 'N/A'}</td>
                    <td>{benefit.benefitType || 'N/A'}</td>
                    <td>{benefit.benefitInternaleRef || 'N/A'}</td>
                    <td>{benefit.benefitDays?.maximum || 'N/A'}</td>
                    <td>{benefit.benefitDays?.minimum || 'N/A'}</td>
                    <td>{benefit.benefitDays?.defaultDays || 'N/A'}</td>
                    <td>{benefit.benefitDays?.isAdjustable ? 'True' : 'False'}</td>
                     <td>{benefit.benefitLimit?.isAdjustable ? 'True' : 'False'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <InfoText>No benefits available.</InfoText>
          )}
        </TableWrapper>
      </Section>

      <AddProductBenefit show={modalShow} onHide={modalClose} fetchBenefits={refetch} />
    </Container>
  );
}